import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useClientPortal } from '../contexts/ClientPortalContext';
import AdaptiveProductCard from './AdaptiveProductCard';
import StockIndicator from './StockIndicator';

const SparePartItem = ({ product, viewMode = 'table' }) => {
    const { orderStarted, cart, addToCart } = useClientPortal();
    const [quantity, setQuantity] = useState(1);
    const [showQuantityInput, setShowQuantityInput] = useState(false);

    const productId = product.item_id || product.id;
    const existingCartItem = cart.find(item => {
        const itemProductId = item.product.item_id || item.product.id;
        return itemProductId === productId;
    });

    const stockLevel = product.stock_on_hand !== undefined ? product.stock_on_hand : (product.available_stock || 0);
    const etaValue = product.cf_in_production_1 || product.custom_field_hash?.cf_in_production_1 || '';
    const isDiscontinuedValue = product.cf_tbd || product.custom_field_hash?.cf_tbd || false;
    const isProductDiscontinued = isDiscontinuedValue === true || isDiscontinuedValue === 'true';
    const isInProduction = etaValue && etaValue !== '';

    const handleAddToCart = () => {
        if (!orderStarted) return;
        if (showQuantityInput) {
            addToCart(product, quantity);
            setShowQuantityInput(false);
            setQuantity(1);
        } else {
            setShowQuantityInput(true);
        }
    };

    const handleQuantityChange = (e) => {
        const value = parseInt(e.target.value) || 1;
        setQuantity(Math.max(1, value));
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') handleAddToCart();
        else if (e.key === 'Escape') {
            setShowQuantityInput(false);
            setQuantity(1);
        }
    };

    const ActionButton = () => (
        orderStarted && (
            showQuantityInput ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '5px' }}>
                    <input
                        type="number"
                        min="1"
                        value={quantity}
                        onChange={handleQuantityChange}
                        onKeyDown={handleKeyPress}
                        autoFocus
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            width: '60px',
                            padding: '8px',
                            borderRadius: '4px',
                            border: '1px solid #4CAF50'
                        }}
                    />
                    <button
                        onClick={(e) => { e.stopPropagation(); handleAddToCart(); }}
                        style={{
                            background: '#4CAF50',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '8px 12px',
                            cursor: 'pointer'
                        }}
                    >
                        Add
                    </button>
                </div>
            ) : (
                <button
                    onClick={handleAddToCart}
                    style={{
                        background: existingCartItem ? '#4CAF50' : '#f0f0f0',
                        color: existingCartItem ? 'white' : '#333',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '8px 16px',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        fontSize: '0.9em',
                        width: '100%'
                    }}
                >
                    {existingCartItem ? `In Cart (${existingCartItem.quantity})` : 'Add to Cart'}
                </button>
            )
        )
    );

    if (viewMode === 'list') {
        return (
            <div style={{ 
                border: '1px solid #eee', 
                borderRadius: '8px', 
                padding: '15px', 
                marginBottom: '10px',
                backgroundColor: existingCartItem ? '#e8f5e8' : 'white',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', alignItems: 'flex-start' }}>
                    <span style={{ fontWeight: 'bold', color: '#555', background: '#f5f5f5', padding: '2px 6px', borderRadius: '4px', fontSize: '0.85em' }}>
                        {product.sku}
                    </span>
                    <StockIndicator stockLevel={stockLevel} etaValue={etaValue} />
                </div>
                
                <div style={{ fontWeight: '600', color: '#333', marginBottom: '10px', fontSize: '1em' }}>
                    {product.name}
                </div>
                
                <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', marginBottom: '12px' }}>
                    {isInProduction && (
                        <span className="production-badge" style={{ fontSize: '0.7em' }}>{etaValue}</span>
                    )}
                    {isProductDiscontinued && (
                        <span className="discontinued-badge" style={{ fontSize: '0.7em' }}>UNTIL STOCK LAST</span>
                    )}
                </div>

                {orderStarted && (
                    <div style={{ marginTop: '10px' }}>
                        <ActionButton />
                    </div>
                )}
            </div>
        );
    }

    return (
        <tr style={{ 
            borderBottom: '1px solid #eee',
            backgroundColor: existingCartItem ? '#e8f5e8' : 'white',
            transition: 'background-color 0.3s'
        }}>
            <td style={{ padding: '12px', fontWeight: 'bold', color: '#555', fontSize: '0.9em' }}>{product.sku}</td>
            <td style={{ padding: '12px' }}>
                <div style={{ fontWeight: '600', color: '#333' }}>{product.name}</div>
                <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                    {isInProduction && (
                        <span className="production-badge" style={{ fontSize: '0.7em', marginTop: '4px' }}>{etaValue}</span>
                    )}
                    {isProductDiscontinued && (
                        <span className="discontinued-badge" style={{ fontSize: '0.7em', marginTop: '4px' }}>UNTIL STOCK LAST</span>
                    )}
                </div>
            </td>
            <td style={{ padding: '12px' }}>
                <StockIndicator stockLevel={stockLevel} etaValue={etaValue} />
            </td>
            {orderStarted && (
                <td style={{ padding: '12px', textAlign: 'right', minWidth: '150px' }}>
                    <ActionButton />
                </td>
            )}
        </tr>
    );
};

const AdaptiveProductGrid = () => {
    const { orderStarted } = useClientPortal();
    const [products, setProducts] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [allCategories, setAllCategories] = useState([]); // État pour toutes les catégories Zoho
    
    // Filtres pour la compatibilité avec l'ancienne logique
    const [filters, setFilters] = useState({
        stockFilter: 'all',
        category: 'all'
    });

    // Références pour le scroll automatique
    const sparePartsRef = useRef(null);
    const kitsRef = useRef(null);
    
    // IDs des kits trouvés par la recherche pour l'animation
    const [highlightedKitIds, setHighlightedKitIds] = useState([]);

    // Pagination pour les pièces détachées
    const [itemsPerPage, setItemsPerPage] = useState(20);
    const [currentPage, setCurrentPage] = useState(1);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        loadProducts();
        loadCategories();
    }, []);

    const loadCategories = async () => {
        try {
            const response = await axios.get('/api/zoho/categories');
            setAllCategories(response.data || []);
        } catch (error) {
            console.error('Error loading categories:', error);
        }
    };

    const loadProducts = async (forceRefresh = false) => {
        setIsLoading(true);
        setError('');
        try {
            // Utiliser l'endpoint catalog qui supporte le cache et le filtrage des catégories
            const url = forceRefresh ? '/api/zoho/products/catalog?refresh=true' : '/api/zoho/products/catalog';
            const response = await axios.get(url);
            const productList = response.data.success ? response.data.products : response.data;
            setProducts(productList || []);
        } catch (error) {
            setError(`Error loading products: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    // Si une recherche est activée, on sélectionne "All Parts" visuellement
    // pour indiquer qu'on cherche dans tout le catalogue
    useEffect(() => {
        const isSearchActive = searchTerm && searchTerm.trim().length >= 3;
        if (isSearchActive && filters.category !== 'all') {
            setFilters(prev => ({
                ...prev,
                category: 'all'
            }));
        }
    }, [searchTerm, filters.category]);

    const handleFilterChange = (filterType, value) => {
        setCurrentPage(1); // Reset pagination

        // Si on change de catégorie (ou de filtre stock), on veut peut-être nettoyer la recherche
        // Le client demande : "si durant la recherche on clique sur une autre catégorie ca supprime la recherche"
        if (filterType === 'category' && searchTerm) {
            setSearchTerm('');
        }

        setFilters(prev => {
            const newFilters = {
                ...prev,
                [filterType]: value
            };
            
            // Logique 1: Si on clique sur "All" dans les filtres de disponibilité, 
            // sélectionner automatiquement "All Parts" dans les catégories SEULEMENT si aucune catégorie n'est sélectionnée
            if (filterType === 'stockFilter' && value === 'all' && prev.category === 'none') {
                newFilters.category = 'all';
            }
            
            // Logique 2: Si on clique sur une catégorie et qu'aucun filtre de disponibilité 
            // n'est sélectionné (none), sélectionner automatiquement "All" dans les filtres de disponibilité
            if (filterType === 'category' && value !== 'none' && prev.stockFilter === 'none') {
                newFilters.stockFilter = 'all';
            }
            
            return newFilters;
        });
    };

    const handleSearchSubmit = () => {
        if (!searchTerm.trim()) {
            // Pas de recherche, on reset et on va au début
            setHighlightedKitIds([]);
            return;
        }

        // Vérifier si la recherche contient au moins 3 caractères significatifs (sans compter HB)
        const searchTermCleaned = searchTerm.toLowerCase().replace(/^hb/i, '').trim();
        if (searchTermCleaned.length < 3) {
            // Recherche trop courte, on ne fait rien
            return;
        }

        // Recherche des kits correspondant au searchTerm (Zoho simple)
        const searchTermLower = searchTerm.toLowerCase();
        const kits = getKitProducts();
        const matchingKits = kits.filter(product => {
            const name = product.name.toLowerCase();
            const sku = product.sku?.toLowerCase() || '';
            return (
                name.includes(searchTermLower) ||
                sku.includes(searchTermLower)
            );
        });

        if (matchingKits.length > 0) {
            // Des kits trouvés : animation et redirection vers les kits
            setHighlightedKitIds(matchingKits.map(p => p.item_id));
            
            // Redirection vers la section kits
            setTimeout(() => {
                kitsRef.current?.scrollIntoView({ 
                    behavior: 'smooth', 
                    block: 'start',
                    inline: 'nearest'
                });
            }, 100);

            // Arrêter l'animation après 3 secondes
            setTimeout(() => {
                setHighlightedKitIds([]);
            }, 3000);
        } else {
            // Aucun kit trouvé : redirection vers spare parts
            setHighlightedKitIds([]);
            setTimeout(() => {
                sparePartsRef.current?.scrollIntoView({ 
                    behavior: 'smooth', 
                    block: 'start',
                    inline: 'nearest'
                });
            }, 100);
        }
    };

    // Obtenir les IDs des sous-catégories de kits
    const getKitCategoryIds = () => {
        // Trouver les catégories mères qui contiennent "KIT"
        const kitParentCategories = allCategories.filter(cat => 
            cat.parent_category_id === "-1" && 
            cat.name.toUpperCase().includes("KIT")
        );
        
        // Obtenir les IDs des sous-catégories
        const kitSubCategoryIds = new Set();
        kitParentCategories.forEach(parent => {
            allCategories.forEach(cat => {
                if (cat.parent_category_id === parent.category_id) {
                    kitSubCategoryIds.add(cat.category_id);
                }
            });
        });
        
        return kitSubCategoryIds;
    };

    // Séparer les kits des autres produits (basé sur les catégories)
    const getKitProducts = () => {
        const kitCategoryIds = getKitCategoryIds();
        return products.filter(product => {
            // Vérifier si le produit appartient à une sous-catégorie de kit
            return product.category_id && kitCategoryIds.has(product.category_id);
        });
    };

    const getFilteredSpareParts = () => {
        let filtered = [...products];

        // Exclure les kits (basé sur les catégories)
        const kitCategoryIds = getKitCategoryIds();
        filtered = filtered.filter(product => {
            // Exclure si le produit appartient à une sous-catégorie de kit
            return !(product.category_id && kitCategoryIds.has(product.category_id));
        });

        // Recherche simple comme dans l'admin (nom ou SKU)
        // SI recherche active et valide : On ignore les filtres et on trie par catégorie
        const hasActiveSearch = searchTerm && searchTerm.trim().length >= 3;
        
        if (hasActiveSearch) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(product =>
                product.name.toLowerCase().includes(term) ||
                (product.sku && product.sku.toLowerCase().includes(term))
            );
            
            // Tri par Catégorie puis par SKU pour le résultat de recherche
            filtered.sort((a, b) => {
                 const catCompare = (a.category_name || '').localeCompare(b.category_name || '');
                 if (catCompare !== 0) return catCompare;
                 
                 const skuA = a.sku || '';
                 const skuB = b.sku || '';
                 return skuA.localeCompare(skuB, undefined, { numeric: true, sensitivity: 'base' });
            });
            
            return filtered;
        }

        // Filtre par catégorie Zoho (category_name)
        if (filters.category !== 'none' && filters.category !== 'all') {
            filtered = filtered.filter(product => 
                product.category_name && product.category_name === filters.category
            );
        }
        
        // Filtre de disponibilité
        switch (filters.stockFilter) {
            case 'all':
                // Afficher tous les spare parts
                break;
            case 'available':
                filtered = filtered.filter(product => {
                    const stockLevel = product.stock_on_hand !== undefined ? product.stock_on_hand : (product.available_stock || 0);
                    return stockLevel > 0;
                });
                break;
            case 'out-of-stock':
                filtered = filtered.filter(product => {
                    const stockLevel = product.stock_on_hand !== undefined ? product.stock_on_hand : (product.available_stock || 0);
                    return stockLevel === 0;
                });
                break;
            case 'none':
            default:
                // Aucun filtre sélectionné, retourner un tableau vide
                return [];
        }

        // Tri par SKU par défaut (hors recherche)
        filtered.sort((a, b) => {
            const skuA = a.sku || '';
            const skuB = b.sku || '';
            return skuA.localeCompare(skuB, undefined, { numeric: true, sensitivity: 'base' });
        });
        return filtered;
    };

    // Vérifier si les spare parts doivent être affichées
    const shouldShowSparePartsProducts = () => {
        // Afficher si :
        // 1. Il y a une recherche active (et valide)
        const hasActiveSearch = searchTerm && searchTerm.trim().length >= 3;
        
        // 2. Un filtre de stock est sélectionné (autre que 'none')
        const hasStockFilter = filters.stockFilter !== 'none';
        
        // 3. Une catégorie est sélectionnée (autre que 'none')
        const hasCategoryFilter = filters.category !== 'none';
        
        return hasActiveSearch || hasStockFilter || hasCategoryFilter;
    };

    const clearFilters = () => {
        setFilters({
            stockFilter: 'none',
            category: 'none'
        });
        setSearchTerm('');
        setCurrentPage(1);
    };

    // Catégories Zoho basées sur category_name (exclut les sous-catégories de kits)
    const getUniqueCategories = () => {
        const kitCategoryIds = getKitCategoryIds();
        
        const categories = products
            .filter(product => product.category_name && product.category_name.trim() !== '')
            .filter(product => !kitCategoryIds.has(product.category_id)) // Exclure les sous-catégories de kits
            .map(product => ({
                id: product.category_id || product.category_name,
                name: product.category_name
            }))
            .filter((category, index, self) => 
                index === self.findIndex(c => c.name === category.name)
            )
            .sort((a, b) => a.name.localeCompare(b.name));
        
        return categories;
    };

    // Statistiques réactives pour les spare parts selon les filtres appliqués
    const getReactiveSparePartsStats = () => {
        // Partir de tous les spare parts (sans le filtre de disponibilité)
        const kitCategoryIds = getKitCategoryIds();
        let spareParts = products.filter(product => {
            // Exclure si le produit appartient à une sous-catégorie de kit
            return !(product.category_id && kitCategoryIds.has(product.category_id));
        });

        // Appliquer la recherche si active
        if (searchTerm && searchTerm.trim().length > 0) {
            const term = searchTerm.toLowerCase();
            spareParts = spareParts.filter(product =>
                product.name.toLowerCase().includes(term) ||
                (product.sku && product.sku.toLowerCase().includes(term))
            );
        }

        // Appliquer le filtre de catégorie si sélectionné
        if (filters.category !== 'none' && filters.category !== 'all') {
            spareParts = spareParts.filter(product => 
                product.category_name && product.category_name === filters.category
            );
        }

        // Calculer les stats sur cette base filtrée
        const total = spareParts.length;
        const available = spareParts.filter(p => {
            const stockLevel = p.stock_on_hand !== undefined ? p.stock_on_hand : (p.available_stock || 0);
            return stockLevel > 0;
        }).length;
        const outOfStock = spareParts.filter(p => {
            const stockLevel = p.stock_on_hand !== undefined ? p.stock_on_hand : (p.available_stock || 0);
            return stockLevel === 0;
        }).length;
        
        return {
            totalSparePartsProducts: total,
            availableSparePartsProducts: available,
            outOfStockSparePartsProducts: outOfStock
        };
    };

    // Statistiques pour l'en-tête
    const getClientStats = () => {
        const total = products.length;
        // Utilise la même logique que AdaptiveProductCard.js
        const available = products.filter(p => {
            const stockLevel = p.stock_on_hand !== undefined ? p.stock_on_hand : (p.available_stock || 0);
            return stockLevel > 0;
        }).length;
        const outOfStock = products.filter(p => {
            const stockLevel = p.stock_on_hand !== undefined ? p.stock_on_hand : (p.available_stock || 0);
            return stockLevel === 0;
        }).length;
        
        // Debug: Log stock information
        if (products.length > 0) {}
        
        return {
            totalProducts: total,
            availableProducts: available,
            outOfStockProducts: outOfStock
        };
    };

    // Pourcentage utilitaire
    const getPercentage = (available, total) => {
        if (total === 0) return '0%';
        return `${Math.round((available / total) * 100)}%`;
    };

    if (isLoading) {
        return (
            <div style={{ 
                textAlign: 'center', 
                padding: '60px 20px',
                backgroundColor: 'white',
                borderRadius: '12px',
                margin: '20px 0',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}>
                <div style={{ fontSize: '3em', marginBottom: '20px' }}>📦</div>
                <h2 style={{ color: '#333', marginBottom: '10px' }}>Loading catalog...</h2>
                <p style={{ color: '#666' }}>Please wait while we fetch product information.</p>
                <div style={{ 
                    width: '200px', 
                    height: '4px', 
                    backgroundColor: '#e0e0e0', 
                    borderRadius: '2px',
                    margin: '20px auto',
                    overflow: 'hidden'
                }}>
                    <div style={{ 
                        width: '50%', 
                        height: '100%', 
                        backgroundColor: '#4CAF50',
                        borderRadius: '2px',
                        animation: 'loading 1.5s ease-in-out infinite'
                    }}></div>
                </div>
            </div>
        );
    }

    const stats = getClientStats();
    const reactiveSparePartsStats = getReactiveSparePartsStats();
    const kitProducts = getKitProducts();
    const sparePartsProducts = getFilteredSpareParts();

    // Pagination logic
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = sparePartsProducts.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(sparePartsProducts.length / itemsPerPage);

    return (
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
            {/* Header avec statistiques */}
            <div className={`stats-container ${orderStarted ? 'order-mode' : ''}`}>
                <div style={{ 
                    textAlign: 'center', 
                    marginBottom: orderStarted ? '20px' : '15px',
                    paddingBottom: '15px'
                }}>
                    <div style={{ 
                        marginBottom: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '10px'
                    }}>
                        {/* Logo removed from availability header as requested */}
                        {orderStarted && (
                            <span style={{ 
                                color: '#4CAF50', 
                                fontSize: '1.2em',
                                fontWeight: 'bold'
                            }}>
                                🛒 Order Mode
                            </span>
                        )}
                    </div>
                    <div className="stats-grid">
                        <div className="stat-item">
                            <div className="stat-value" style={{ color: '#1D1D1B' }}>
                                {stats.totalProducts}
                            </div>
                            <div className="stat-label">Total Active Items</div>
                        </div>
                        <div className="stat-item">
                            <div className="stat-value" style={{ color: '#239130ff' }}>
                                {stats.availableProducts}
                            </div>
                            <div className="stat-label">Available</div>
                        </div>
                        {stats.outOfStockProducts > 0 && (
                            <div className="stat-item">
                                <div className="stat-value" style={{ color: '#ff9800' }}>
                                    {stats.outOfStockProducts}
                                </div>
                                <div className="stat-label">Not Available</div>
                            </div>
                        )}
                    </div>
                    {/* In-stock rate visually prominent */}
                    <div className="availability-section">
                        <div className="availability-title">
                            AVAILABILITY RATE
                        </div>
                        <div className="availability-bar-wrapper">
                            <div className="availability-bar-container">
                                <div className="availability-bar-fill" style={{ width: `${stats.totalProducts === 0 ? 0 : (stats.availableProducts / stats.totalProducts) * 100}%` }}></div>
                                {/* Arrow indicator */}
                                <div className="availability-arrow" style={{
                                    left: `${stats.totalProducts === 0 ? 0 : (stats.availableProducts / stats.totalProducts) * 100}%`
                                }}>
                                    ➤
                                </div>
                            </div>
                            <span className="availability-percentage">{getPercentage(stats.availableProducts, stats.totalProducts)}</span>
                        </div>
                    </div>
                    
                    <p style={{ 
                        marginTop: '20px', 
                        fontSize: '0.9rem', 
                        color: '#333', 
                        lineHeight: '1.5',
                        fontWeight: 'bold',
                        maxWidth: '1000px',
                        marginLeft: 'auto',
                        marginRight: 'auto'
                    }}>
                        TEAM HOTBODIES's stock availability report is Live and only serves to provide information of our products stock availability at our Factory and/or logistic hub. Please note that it's not the stock level at TEAM HOTBODIES dealers or national distributors. We invite you to contact your local shop (dealer) in your area or our national distributor for your TEAM HOTBODIES kit and/or spare part availability. All our products can also be purchased on our online store if eligible in your area.
                    </p>
                </div>

                {orderStarted && (
                    <div style={{
                        background: '#d4edda',
                        color: '#155724',
                        padding: '15px',
                        borderRadius: '8px',
                        fontSize: '14px',
                        border: '1px solid #c3e6cb',
                        textAlign: 'center'
                    }}>
                        <strong>📦 Order Mode Active:</strong> Click on products to add them to your cart. 
                        You can specify quantities and review your order before submitting.
                    </div>
                )}
            </div>

            {/* Barre de recherche - sticky */}
            <div style={{ 
                position: 'sticky',
                top: '20px',
                zIndex: 50,
                marginBottom: '20px',
                backgroundColor: 'transparent',
                padding: '15px 20px',
                borderRadius: '12px',
                boxShadow: 'none',
                border: orderStarted ? '2px solid #DA291C' : 'none'
            }}>
                <form onSubmit={(e) => { e.preventDefault(); handleSearchSubmit(); }}>
                    <div style={{ position: 'relative', maxWidth: '600px', margin: '0 auto' }}>
                        <input
                            type="text"
                            placeholder={orderStarted ? "🛒 Search by product name or SKU (min 3 chars, HB=0)..." : "🔍 Search by product name or SKU (min 3 chars, HB=0)..."}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '15px 20px',
                                fontSize: '16px',
                                border: orderStarted ? '2px solid #DA291C' : '2px solid #000000',
                                borderRadius: '25px',
                                outline: 'none',
                                transition: 'border-color 0.3s ease',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}
                            onFocus={(e) => {
                                if (!orderStarted) {
                                    e.target.style.borderColor = '#DA291C';
                                }
                            }}
                            onBlur={(e) => {
                                if (!orderStarted) {
                                    e.target.style.borderColor = '#000000';
                                }
                            }}
                        />
                    </div>
                </form>
            </div>

            {/* Résumé de recherche global */}
            {searchTerm && (() => {
                // Vérifier si la recherche contient au moins 3 caractères significatifs (sans compter HB)
                const searchTermCleaned = searchTerm.toLowerCase().replace(/^hb/i, '').trim();
                return searchTermCleaned.length >= 3;
            })() && (
                <div style={{ 
                    textAlign: 'center', 
                    padding: '15px',
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    fontSize: '14px',
                    color: '#666',
                    marginBottom: '30px'
                }}>
                    📋 {sparePartsProducts.length} spare part(s) and 🧰 {(() => {
                        const searchTermLower = searchTerm.toLowerCase();
                        const kits = getKitProducts();
                        return kits.filter(product => {
                            const name = product.name.toLowerCase();
                            const sku = product.sku?.toLowerCase() || '';
                            return (
                                name.includes(searchTermLower) ||
                                sku.includes(searchTermLower)
                            );
                        }).length;
                    })()} kit(s) found for "{searchTerm}"
                </div>
            )}

            {/* Error Display */}
            {error && (
                <div style={{
                    background: '#f8d7da',
                    color: '#721c24',
                    padding: '15px',
                    borderRadius: '8px',
                    marginBottom: '20px',
                    border: '1px solid #f5c6cb'
                }}>
                    ❌ {error}
                </div>
            )}

            {/* Affichage des produits avec sections séparées */}
            {(() => {
                const hasKits = kitProducts.length > 0;
                const shouldShowSpareParts = shouldShowSparePartsProducts();

                // Si aucune section n'a de contenu à afficher
                if (!hasKits && !shouldShowSpareParts) {
                    return (
                        <div style={{ 
                            textAlign: 'center', 
                            padding: '60px 20px',
                            backgroundColor: 'white',
                            borderRadius: '12px',
                            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                        }}>
                            <div style={{ fontSize: '4em', marginBottom: '20px' }}>🏎️</div>
                            <h2 style={{ color: '#333', marginBottom: '10px' }}>Welcome to the Product Catalog</h2>
                            <p style={{ color: '#666', marginBottom: '20px' }}>
                                Browse our complete kits above or use the spare parts filters below to find specific components.
                            </p>
                        </div>
                    );
                }

                return (
                    <>
                        {/* Section Kits - toujours affichée si des kits existent */}
                        {hasKits && (
                            <div ref={kitsRef} style={{ marginBottom: '40px' }}>
                                <div style={{
                                    background: 'white',
                                    padding: '15px',
                                    borderRadius: '10px',
                                    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.08), inset 4px 0 0 #000000ff',
                                    border: '1px solid #e0e0e0'
                                }}>
                                    <h3 style={{
                                        margin: '0 0 10px 0',
                                        color: '#333',
                                        fontSize: '1.3em',
                                        fontWeight: 'bold'
                                    }}>
                                        KITS
                                    </h3>
                                    <p style={{
                                        margin: 0,
                                        color: '#666',
                                        fontSize: '0.9em'
                                    }}>
                                        Complete kits ({kitProducts.length} items)
                                    </p>
                                    {/* Pourcentage kits disponibles */}
                                    <div style={{ marginTop: '8px', fontSize: '0.95em', color: '#202020ff', fontWeight: 'bold' }}>
                                        {`Available kits: ${getPercentage(kitProducts.filter(p => {
                                            const stockLevel = p.stock_on_hand !== undefined ? p.stock_on_hand : (p.available_stock || 0);
                                            return stockLevel > 0;
                                        }).length, kitProducts.length)}`}
                                    </div>
                                </div>
                                <div className="product-grid">
                                    {kitProducts.map(product => (
                                        <AdaptiveProductCard
                                            key={product.item_id}
                                            product={product}
                                            highlight={highlightedKitIds.includes(product.item_id)}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}

        {/* Section Pièces Détachées - Toujours afficher le titre et les filtres */}
        <div ref={sparePartsRef} style={{ marginBottom: '40px' }}>
            <div style={{
                background: 'white',
                padding: '15px',
                borderRadius: '10px',
                boxShadow: '0 2px 6px rgba(0, 0, 0, 0.08), inset 4px 0 0 #000000ff',
                border: '1px solid #e0e0e0',
                marginBottom: '15px'
            }}>
                <h3 style={{
                    margin: '0 0 10px 0',
                    color: '#333',
                    fontSize: '1.3em',
                    fontWeight: 'bold'
                }}>
                    ALL PARTS BY KITS
                </h3>
                <p style={{
                    margin: '0 0 15px 0',
                    color: '#666',
                    fontSize: '0.9em'
                }}>
                    Select filters to browse spare parts
                </p>
                
               

                {/* Filtres intégrés dans la boîte */}
                <div className="spare-parts-filters">
                    {/* Filtres de disponibilité */}
                    <div>
                        {[ 
                            { key: 'all', label: 'Total Parts', count: reactiveSparePartsStats.totalSparePartsProducts },
                            { key: 'available', label: '📦 Available', count: reactiveSparePartsStats.availableSparePartsProducts },
                            { key: 'out-of-stock', label: '⏳ Not Available', count: reactiveSparePartsStats.outOfStockSparePartsProducts }
                        ].map(filter => (
                            <button
                                key={filter.key}
                                onClick={() => handleFilterChange('stockFilter', filter.key)}
                                className={`filter-button short-text ${filters.stockFilter === filter.key ? 'active' : ''}`}
                                title={`${filter.label} (${filter.count})`}
                            >
                                {filter.label} ({filter.count})
                            </button>
                        ))}
                    </div>

                    {/* Filtre par catégories */}
                    {getUniqueCategories().length > 0 && (
                        <div>
                            <button
                                onClick={() => handleFilterChange('category', 'all')}
                                className={`filter-button short-text ${filters.category === 'all' ? 'active' : ''}`}
                                data-filter="all"
                            >
                                All Parts
                            </button>
                            {getUniqueCategories().map(category => (
                                <button
                                    key={category.id}
                                    onClick={() => handleFilterChange('category', category.name)}
                                    className={`filter-button ${filters.category === category.name ? 'active' : ''}`}
                                    title={category.name}
                                >
                                    {category.name}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Reset button if filters are active */}
                {(searchTerm || filters.stockFilter !== 'none' || filters.category !== 'none') && (
                    <div style={{ textAlign: 'center', marginTop: '15px' }}>
                        <button
                            onClick={clearFilters}
                            style={{
                                padding: '8px 16px',
                                backgroundColor: '#ff6b6b',
                                color: 'white',
                                border: 'none',
                                borderRadius: '15px',
                                cursor: 'pointer',
                                fontSize: '12px',
                                transition: 'all 0.3s ease'
                            }}
                        >
                            🗑️ Reset Filters
                        </button>
                    </div>
                )}
            </div>
            
            <div className="spare-parts-container">
                {/* Afficher les stats de la section si des filtres sont actifs */}
                {shouldShowSparePartsProducts() && (
                    <div style={{
                        marginTop: '10px',
                        padding: '10px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '8px',
                        fontSize: '0.9em'
                    }}>
                        <div style={{ color: '#666' }}>
                            {(searchTerm && searchTerm.trim().length >= 3)
                                ? `Search results for "${searchTerm}" (${sparePartsProducts.length} items)`
                                : filters.category === 'all' 
                                ? `All spare parts (${sparePartsProducts.length} items)`
                                : filters.category !== 'none'
                                ? `${getUniqueCategories().find(c => c.name === filters.category)?.name || 'Selected category'} (${sparePartsProducts.length} items)`
                                : `Filtered spare parts (${sparePartsProducts.length} items)`
                            }
                        </div>
                        {/* Pourcentage spare parts réactif */}
                        <div style={{ marginTop: '5px', fontSize: '0.95em', color: '#202020ff', fontWeight: 'bold' }}>
                            {`Available spare parts: ${getPercentage(sparePartsProducts.filter(p => {
                                const stockLevel = p.stock_on_hand !== undefined ? p.stock_on_hand : (p.available_stock || 0);
                                return stockLevel > 0;
                            }).length, sparePartsProducts.length)}`}
                        </div>
                    </div>
                )}

                {/* Affichage conditionnel des produits */}
                {shouldShowSparePartsProducts() ? (
                    sparePartsProducts.length > 0 ? (
                        <>
                            {/* Pagination Selector */}
                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px', alignItems: 'center', gap: '10px' }}>
                                <label style={{ fontSize: '0.9em', color: '#666' }}>Items per page:</label>
                                <select 
                                    value={itemsPerPage} 
                                    onChange={(e) => {
                                        setItemsPerPage(Number(e.target.value));
                                        setCurrentPage(1);
                                    }}
                                    style={{ padding: '5px', borderRadius: '4px', border: '1px solid #ddd' }}
                                >
                                    <option value={20}>20</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                </select>
                            </div>

                            <div style={{ marginTop: '15px' }}>
                                {isMobile ? (
                                    <div className="mobile-list-view">
                                        {currentItems.map((product, index) => {
                                            const isSearchActive = searchTerm && searchTerm.trim().length >= 3;
                                            const showCategoryHeader = isSearchActive && (index === 0 || product.category_name !== currentItems[index - 1].category_name);
                                            const categoryCount = showCategoryHeader ? sparePartsProducts.filter(p => p.category_name === product.category_name).length : 0;

                                            return (
                                                <React.Fragment key={product.item_id}>
                                                    {showCategoryHeader && (
                                                        <div style={{
                                                            padding: '10px 15px',
                                                            backgroundColor: '#eaeaea',
                                                            color: '#1d1d1b',
                                                            fontWeight: 'bold',
                                                            borderBottom: '1px solid #ddd',
                                                            marginTop: index > 0 ? '5px' : '0',
                                                            borderRadius: '4px'
                                                        }}>
                                                            {product.category_name || 'Other'} ({categoryCount} items)
                                                        </div>
                                                    )}
                                                    <SparePartItem product={product} viewMode="list" />
                                                </React.Fragment>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div style={{ overflowX: 'auto' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
                                            <thead>
                                                <tr style={{ borderBottom: '2px solid #eee', textAlign: 'left', color: '#666' }}>
                                                    <th style={{ padding: '12px', width: '15%' }}>SKU</th>
                                                    <th style={{ padding: '12px', width: '45%' }}>Product Name</th>
                                                    <th style={{ padding: '12px', width: '25%' }}>Availability</th>
                                                    {orderStarted && <th style={{ padding: '12px', width: '15%', textAlign: 'right' }}>Action</th>}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {currentItems.map((product, index) => {
                                                     const isSearchActive = searchTerm && searchTerm.trim().length >= 3;
                                                     const showCategoryHeader = isSearchActive && (index === 0 || product.category_name !== currentItems[index - 1].category_name);
                                                     const categoryCount = showCategoryHeader ? sparePartsProducts.filter(p => p.category_name === product.category_name).length : 0;

                                                     return (
                                                        <React.Fragment key={product.item_id}>
                                                            {showCategoryHeader && (
                                                                <tr style={{ backgroundColor: '#f9f9f9' }}>
                                                                    <td colSpan={orderStarted ? 4 : 3} style={{
                                                                        padding: '10px 15px',
                                                                        backgroundColor: '#eaeaea',
                                                                        color: '#1d1d1b',
                                                                        fontWeight: 'bold',
                                                                        fontSize: '0.95em'
                                                                    }}>
                                                                        {product.category_name || 'Other'} ({categoryCount} items)
                                                                    </td>
                                                                </tr>
                                                            )}
                                                            <SparePartItem product={product} viewMode="table" />
                                                        </React.Fragment>
                                                     );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>

                            {/* Pagination Controls */}
                            {totalPages > 1 && (
                                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                                    <button 
                                        onClick={() => {
                                            setCurrentPage(1);
                                            sparePartsRef.current?.scrollIntoView({ behavior: 'smooth' });
                                        }}
                                        disabled={currentPage === 1}
                                        style={{
                                            padding: '8px 12px',
                                            border: '1px solid #ddd',
                                            borderRadius: '4px',
                                            background: currentPage === 1 ? '#f5f5f5' : 'white',
                                            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                                            fontWeight: 'bold',
                                            minWidth: '40px'
                                        }}
                                        title="First Page"
                                    >
                                        &laquo;
                                    </button>
                                    <button 
                                        onClick={() => {
                                            setCurrentPage(prev => Math.max(prev - 1, 1));
                                            sparePartsRef.current?.scrollIntoView({ behavior: 'smooth' });
                                        }}
                                        disabled={currentPage === 1}
                                        style={{
                                            padding: '8px 16px',
                                            border: '1px solid #ddd',
                                            borderRadius: '4px',
                                            background: currentPage === 1 ? '#f5f5f5' : 'white',
                                            cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                                        }}
                                    >
                                        Previous
                                    </button>
                                    <span style={{ padding: '5px 10px', color: '#333', whiteSpace: 'nowrap' }}>
                                        Page {currentPage} of {totalPages}
                                    </span>
                                    <button 
                                        onClick={() => {
                                            setCurrentPage(prev => Math.min(prev + 1, totalPages));
                                            sparePartsRef.current?.scrollIntoView({ behavior: 'smooth' });
                                        }}
                                        disabled={currentPage === totalPages}
                                        style={{
                                            padding: '8px 16px',
                                            border: '1px solid #ddd',
                                            borderRadius: '4px',
                                            background: currentPage === totalPages ? '#f5f5f5' : 'white',
                                            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
                                        }}
                                    >
                                        Next
                                    </button>
                                    <button 
                                        onClick={() => {
                                            setCurrentPage(totalPages);
                                            sparePartsRef.current?.scrollIntoView({ behavior: 'smooth' });
                                        }}
                                        disabled={currentPage === totalPages}
                                        style={{
                                            padding: '8px 12px',
                                            border: '1px solid #ddd',
                                            borderRadius: '4px',
                                            background: currentPage === totalPages ? '#f5f5f5' : 'white',
                                            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                                            fontWeight: 'bold',
                                            minWidth: '40px'
                                        }}
                                        title="Last Page"
                                    >
                                        &raquo;
                                    </button>
                                </div>
                            )}
                        </>
                    ) : (
                        <div style={{ 
                            textAlign: 'center', 
                            padding: '40px 20px',
                            backgroundColor: 'white',
                            borderRadius: '12px',
                            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                            marginTop: '20px'
                        }}>
                            <div style={{ fontSize: '3em', marginBottom: '15px' }}>🔍</div>
                            <h3 style={{ color: '#333', marginBottom: '10px' }}>No spare parts found</h3>
                            <p style={{ color: '#666' }}>
                                {searchTerm 
                                    ? `No spare parts match "${searchTerm}"`
                                    : 'No spare parts available with the selected filters'
                                }
                            </p>
                        </div>
                    )
                ) : (
                    <div style={{ 
                        textAlign: 'center', 
                        padding: '40px 20px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '12px',
                        marginTop: '20px',
                        border: '2px dashed #e0e0e0'
                    }}>
                        <div style={{ fontSize: '3em', marginBottom: '15px' }}>🔧</div>
                        <h3 style={{ color: '#666', marginBottom: '10px' }}>Select filters to browse spare parts</h3>
                        <p style={{ color: '#999', fontSize: '0.9em' }}>
                            Use the availability filters or category buttons above to view spare parts
                        </p>
                    </div>
                )}
            </div>
        </div>
                    </>
                );
            })()}

            {/* CSS inline pour l'animation */}
            <style>{`
                @keyframes loading {
                    0% { transform: translateX(-100%); }
                    50% { transform: translateX(200%); }
                    100% { transform: translateX(-100%); }
                }
            `}</style>
        </div>
    );
};

export default AdaptiveProductGrid;
